import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ReactComponent as NoContentSvg } from '../assets/icons/no-content.svg';
import { TableProps } from './Table.types';
import { omit } from 'lodash';
import { TableHeader } from './TableHeader';
import { TableRows } from './TableRows';
import { Pagination } from '../Pagination/Pagination';
import './Table.scss';
import { twMerge } from '@shared/utils/twMerge';

const defaultNoContentMessage = (
  <div className="flex h-full w-full justify-center">
    <div className="flex flex-col items-center justify-center">
      <NoContentSvg />
      <div className="text-pastelBlue mt-1.5 text-lg leading-10">There is nothing here yet</div>
    </div>
  </div>
);

const loadingPaginationSkeleton = () => (
  <div className="mt-[2rem] flex h-2 justify-between">
    <div className="flex items-center justify-around">
      <span className="bg-brightGray mr-[1.2rem] h-2 w-4 animate-pulse rounded-[5px]"></span>
      <span className={`bg-brightGray h-4 w-8 animate-pulse rounded-[5px]`}></span>
      <span className="bg-brightGray ml-[1.2rem] h-2 w-[9rem] animate-pulse rounded-[5px]"></span>
    </div>
    <div className={'flex items-center justify-between'}>
      <span className="bg-brightGray mr-[1.2rem] h-2 w-[10rem] animate-pulse rounded-[5px]"></span>
      <div className={'flex items-center justify-between'}>
        <span className="bg-brightGray mr-[0.8rem] h-[3.2rem] w-[3.2rem] rounded-full"></span>
        <span className="bg-brightGray mr-[0.8rem] h-[3.2rem] w-[3.2rem] rounded-full"></span>
        <span className="bg-brightGray mr-[0.8rem] h-[3.2rem] w-[3.2rem] rounded-full"></span>
        <span className="bg-brightGray mr-[0.8rem] h-[3.2rem] w-[3.2rem] rounded-full"></span>
        <span className="bg-brightGray mr-[0.8rem] h-[3.2rem] w-[3.2rem] rounded-full"></span>
        <span className="bg-brightGray h-[3.2rem] w-[3.2rem] rounded-full"></span>
      </div>
    </div>
  </div>
);

export const Table = <T,>({
  data = [],
  id,
  columns = [],
  onSort,
  loading,
  className,
  styles,
  totalItems,
  onHighlightFinish,
  highlightDuration,
  noContentMessage = defaultNoContentMessage,
  loadingLength = 10,
  sortState,
  pagination,
  pageSizeOptions,
  shouldHighlightRow,
  isRowDisabled,
  onRowClick,
  darkMode,
  fullHeight,
}: TableProps<T>) => {
  const ref = useRef<HTMLDivElement>(null);

  const calculateHeight = useCallback(() => {
    return (
      window.innerHeight -
      Number(ref.current?.getBoundingClientRect().y || 0) -
      (pagination?.enabled ? 100 : 0)
    );
  }, [pagination]);

  const [height, setHeight] = useState(calculateHeight);

  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      // NOTE: the second parameter is not necessary.
      // The correct value wil be invoked under the hood.
      pagination?.setCurrentPage(page, pageSize);
      // NOTE: the second parameter is not necessary.
      // The correct value wil be invoked under the hood.
      pagination?.setPageSize(pageSize, page);
    },
    [pagination]
  );

  useEffect(() => {
    if (fullHeight) {
      return;
    }
    setTimeout(() => {
      setHeight(calculateHeight());
    });
    const handleResize = () => {
      setHeight(calculateHeight());
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calculateHeight, fullHeight]);

  return (
    <>
      <div
        ref={ref}
        className={twMerge('flex grow flex-col overflow-auto', className)}
        style={fullHeight ? undefined : { height: `${height}px` }}
      >
        <table
          id={id}
          // TODO: maybe remove mini from here
          className={twMerge('mini:min-w-[600px] w-full min-w-[800px] table-fixed', styles?.table)}
        >
          <TableHeader
            sortState={sortState}
            darkMode={darkMode}
            styles={styles}
            columns={columns.filter(c => !c.hide)}
            onSort={onSort}
          />
          <tbody className={styles?.tbody}>
            <TableRows
              darkMode={darkMode}
              data={data}
              styles={styles}
              onHighlightFinish={onHighlightFinish}
              highlightDuration={highlightDuration}
              loadingLength={loadingLength}
              columns={columns.filter(c => !c.hide)}
              loading={loading}
              shouldHighlightRow={shouldHighlightRow}
              isRowDisabled={isRowDisabled}
              onRowClick={onRowClick}
            />
          </tbody>
        </table>
        {!data?.length && !loading && noContentMessage}
      </div>

      {pagination?.enabled &&
        (loading ? (
          loadingPaginationSkeleton()
        ) : (
          <div className="mini:mt-1 mt-2 w-full">
            <Pagination
              darkMode={darkMode}
              currentPage={pagination?.currentPage}
              currentPageSize={pagination?.pageSize}
              onChange={handlePaginationChange}
              total={Number(totalItems)}
              pageSizeOptions={pageSizeOptions}
            />
          </div>
        ))}
    </>
  );
};

export const useSortTable = <T extends keyof Record<string, string>>(
  defaultValue?: Partial<Record<T, string>>
) => {
  const [currentSort, setCurrentSort] = useState<Partial<Record<T, string>>>(defaultValue || {});
  const sortItems = (name: string, direction: string) => {
    setCurrentSort(prev => {
      if (direction === 'none') {
        return { ...prev, ...omit(prev, name) };
      }
      return {
        ...prev,
        [name]: direction,
      };
    });
  };

  return {
    currentSort,
    sortItems,
  };
};
